import { useTranslation } from 'react-i18next';

import { PathwayModal } from '@/components/popups/PathwayModal';

import { DueDateFormFields } from './DueDateFormFields';
import { useDueDateForm } from './useDueDateForm';

interface DueDatePopUpBaseProps {
  onSave: (date: string | null) => void;
  onBack?: () => void;
}

export interface DueDatePopUpStepProps extends DueDatePopUpBaseProps {
  /** Renders as slot fragment for use inside a parent PathwayModal */
  isStep: true;
}

export interface DueDatePopUpStandaloneProps extends DueDatePopUpBaseProps {
  isStep?: false;
  isOpen: boolean;
  onClose: () => void;
}

export type DueDatePopUpProps =
  | DueDatePopUpStepProps
  | DueDatePopUpStandaloneProps;

const DueDatePopUpContent: React.FC<DueDatePopUpBaseProps> = ({
  onSave,
  onBack,
}) => {
  const { t } = useTranslation();
  const {
    canSave,
    checkboxClicked,
    disableDatePicker,
    getSaveValue,
    onCheckboxClick,
    onDateChange,
  } = useDueDateForm();

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>
          {t('TrainingPlans.accountability.setDueDate')}
        </PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        <DueDateFormFields
          checkboxClicked={checkboxClicked}
          disableDatePicker={disableDatePicker}
          onCheckboxClick={onCheckboxClick}
          onDateChange={onDateChange}
        />
      </PathwayModal.Body>
      <PathwayModal.Footer>
        {onBack ? (
          <PathwayModal.SecondaryButton onPress={onBack}>
            {t('Button.back')}
          </PathwayModal.SecondaryButton>
        ) : null}
        <PathwayModal.PrimaryButton
          color="secondary"
          testId="ButtonSave"
          isDisabled={!canSave}
          onPress={() => onSave(getSaveValue())}
          variant="filled"
        >
          {t('Button.save')}
        </PathwayModal.PrimaryButton>
      </PathwayModal.Footer>
    </>
  );
};

const DueDatePopUpStandalone: React.FC<DueDatePopUpStandaloneProps> = ({
  isOpen,
  onClose,
  onSave,
  onBack,
}) => (
  <PathwayModal
    isOpen={isOpen}
    onClose={onClose}
    className="2xl:max-w-[610px]!"
    stepKey="due-date-popup"
  >
    <DueDatePopUpContent onSave={onSave} onBack={onBack} />
  </PathwayModal>
);

const DueDatePopUp: React.FC<DueDatePopUpProps> = props => {
  if (props.isStep) {
    return <DueDatePopUpContent onSave={props.onSave} onBack={props.onBack} />;
  }

  return <DueDatePopUpStandalone {...props} />;
};

export default DueDatePopUp;
